<script setup>
import BaseLayout from "@/components/BaseLayout.vue";
import { getAuth } from "firebase/auth";
import { billingMode } from "@/lib/billingMode";

import { ref, computed, onMounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/firebase/firebase";
import { app } from "@/firebase/firebase";
import { loadRobotsFromFirestore } from "@/lib/robotsService";
import { Capacitor } from '@capacitor/core'
import * as Purchases from '@revenuecat/purchases-capacitor'

const isNativeIOS =
  typeof Capacitor?.getPlatform === 'function' &&
  Capacitor.getPlatform() === 'ios' &&
  typeof Capacitor?.isNativePlatform === 'function' &&
  Capacitor.isNativePlatform()

const iapLoading = ref(false)
const iapError = ref('')
const iosSubscribed = ref(false)        // based on IAP entitlements
const iosManageUrl = ref(null)          // Apple management URL
const iosPackage = ref(null)            // RevenueCat package to buy

onMounted(async () => {
  if (!isNativeIOS) return
  try {
    iapLoading.value = true

    // 1) Entitlements (are we already subscribed via IAP?)
    const info = await Purchases.getCustomerInfo()
    const active = info?.customerInfo?.entitlements?.active || {}
    iosSubscribed.value = Object.keys(active).length > 0
    iosManageUrl.value = info?.customerInfo?.managementURL || 'https://apps.apple.com/account/subscriptions'

    // 2) Offerings → pick the default/current package
    const offerings = await Purchases.getOfferings()
    const current = offerings?.current
    const pkg = current?.availablePackages?.[0] || null
    iosPackage.value = pkg
  } catch (e) {
    iapError.value = e?.message || 'Store unavailable'
  } finally {
    iapLoading.value = false
  }
})

async function purchaseStandardIos () {
  if (!iosPackage.value) { alert('Store unavailable. Please try again.'); return }
  try {
    iapLoading.value = true
    iapError.value = ''
    const { customerInfo } = await Purchases.purchasePackage(iosPackage.value)
    const active = customerInfo?.entitlements?.active || {}
    iosSubscribed.value = Object.keys(active).length > 0
  } catch (e) {
    // User cancellations come through as errors too; don’t alert noisy errors
    if (e?.userCancelled) return
    iapError.value = e?.message || 'Purchase failed'
    alert(iapError.value)
  } finally {
    iapLoading.value = false
  }
}

function manageSubscriptionIos () {
  const url = iosManageUrl.value || 'https://apps.apple.com/account/subscriptions'
  window.location.href = url
}


const mode = billingMode();
const isWeb = mode === "web";

/* ---------- Plan state (store + DB) ---------- */
const auth = useAuthStore();
const planFromStore = computed(() => auth.currentUser?.plan || "free");

const planLoading = ref(true);
const planFromDb = ref(null);

onMounted(async () => {
  try {
    const user = getAuth().currentUser;
    if (user) {
      const snap = await getDoc(doc(db, "users", user.uid));
      planFromDb.value = snap.exists() ? (snap.data().plan || "free") : "free";
    } else {
      planFromDb.value = "free";
    }
  } catch {
    planFromDb.value = planFromStore.value || "free";
  } finally {
    planLoading.value = false;
  }
});

const plan = computed(() => (planFromDb.value ?? planFromStore.value));
const isSubscribed = computed(() => plan.value !== "free");

/* ---------- Robots from Firestore ---------- */
const robotsMap = ref({});
onMounted(async () => {
  const loaded = await loadRobotsFromFirestore?.();
  if (loaded && Object.keys(loaded).length) robotsMap.value = loaded;
});

const robotsList = computed(() => {
  const out = [];
  for (const [id, r] of Object.entries(robotsMap.value || {})) {
    const rawTier = r.tier ?? r.plan ?? r.pricing?.tier ?? r.accessTier;
    const tier = rawTier === "free" ? "free" : (rawTier === "pro" ? "pro" : "standard");
    out.push({ id, tier, label: r.name || r.model || id });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
});

const freeRobots = computed(() => robotsList.value.filter(r => r.tier === "free"));
const standardRobots = computed(() =>
  robotsList.value.filter(r => r.tier === "standard" || r.tier === "free")
);

// Expand/collapse
const showFreeRobots = ref(false);
const showStdRobots  = ref(false);

/* ---------- Stripe ---------- */
const ENDPOINT =
  "https://us-central1-robot-cycle-time-ai.cloudfunctions.net/createCheckoutSessionHttp";
const PORTAL_HTTP =
  "https://us-central1-robot-cycle-time-ai.cloudfunctions.net/createBillingPortalSessionHttp";

async function startCheckout() {
  if (isSubscribed.value) { window.location.href = "/account"; return; }
  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error("Please sign in first.");
    const idToken = await user.getIdToken();

    const snap = await getDoc(doc(db, "users", user.uid));
    const planNow = (snap.exists() && snap.data().plan) || "free";
    if (planNow !== "free") { window.location.href = "/account"; return; }

    const resp = await fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ priceId: import.meta.env.VITE_STRIPE_PRICE_ID, origin: window.location.origin })
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${await resp.text()}`);
    const { url } = await resp.json();
    if (!url) throw new Error("No URL from server");
    window.location.href = url;
  } catch (err) {
    console.error("Checkout failed:", err);
    alert(`Checkout failed: ${err.message || err}`);
  }
}

const fns = getFunctions(app, import.meta.env.VITE_FIREBASE_FUNCTIONS_REGION || "us-central1");
async function openBillingPortal() {
  const returnUrl = window.location.origin + "/account";
  try {
    const fn = httpsCallable(fns, "createBillingPortalSession");
    const { data } = await fn({ returnUrl });
    if (data?.url) { window.location.href = data.url; return; }
  } catch (_) { /* fall through */ }
  try {
    const user = getAuth().currentUser;
    if (!user) throw new Error("Please sign in first.");
    const idToken = await user.getIdToken();
    const resp = await fetch(PORTAL_HTTP, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({ returnUrl })
    });
    if (resp.ok) {
      const { url } = await resp.json();
      if (url) { window.location.href = url; return; }
    }
  } catch (_) {}
  window.location.href = "/account";
}async function restoreIos () {
  try {
    iapLoading.value = true
    iapError.value = ''
    const { customerInfo } = await Purchases.restorePurchases()
    const active = customerInfo?.entitlements?.active || {}
    iosSubscribed.value = Object.keys(active).length > 0
  } catch (e) {
    iapError.value = e?.message || 'Restore failed'
  } finally {
    iapLoading.value = false
  }
}


/* ---------- Subtitle ---------- */
const subtitle = computed(() => {
  if (plan.value === "standard") return "You’re on Standard";
  if (plan.value === "pro") return "You’re on Pro";
  return "Upgrade to Standard";
});
</script>

<template>
  <BaseLayout title="Pricing" :subtitle="subtitle" :narrow="false">
    <div class="plans-grid">
      <!-- Free -->
      <section class="plan-card">
        <header class="plan-head">
          <div class="plan-title">
            <div class="name">Free</div>
          </div>
          <div class="cta-area" aria-hidden="true"></div>
        </header>

        <ul class="bullets">
          <li><strong>3</strong> Calibrated robots</li>
          <li><strong>AI tools:</strong> basic helpers</li>
          <li><strong>Motion builder:</strong> Waypoints & Segments</li>
          <li><strong>Customization:</strong> delays, approach offset, blended motion toggle</li>
          <li><strong>Plan:</strong> Free</li>
        </ul>

        <!-- Robots -->
        <div class="robots-wrap">
          <button class="expander" type="button" @click="showFreeRobots = !showFreeRobots" :aria-expanded="showFreeRobots">
            <span class="chev" :class="{open: showFreeRobots}">▸</span>
            Robots
          </button>
          <transition name="fade">
            <div v-if="showFreeRobots" class="chips">
              <span v-for="r in freeRobots" :key="r.id" class="chip">{{ r.label }}</span>
            </div>
          </transition>
        </div>

        <p class="foot-note">Great for quick concept checks.</p>
      </section>

      <!-- Standard -->
      <section class="plan-card highlight">
        <header class="plan-head">
          <div class="plan-title">
            <div class="name">Standard</div>
            <div class="price-inline"><span class="price">$19.99</span><span class="per">/month</span></div>
          </div>
<div class="cta-area">
  <!-- Web (Stripe) — unchanged -->
  <div v-if="isWeb && !planLoading">
    <template v-if="isSubscribed">
      <button type="button" class="btn-primary" @click="openBillingPortal">Manage billing</button>
      <div class="status-pill">✓ Subscribed</div>
    </template>
    <template v-else>
      <button type="button" class="btn-primary" @click="startCheckout">Subscribe</button>
    </template>
  </div>

<!-- iOS (IAP via RevenueCat) -->
<div v-else-if="isNativeIOS" class="cta-ios">
  <!-- IAP active -->
  <template v-if="iosSubscribed">
    <button type="button" class="btn-primary" @click="manageSubscriptionIos">Manage subscription</button>
    <div class="status-pill">✓ Subscribed</div>
  </template>

  <!-- Paid via Stripe but no IAP -->
  <template v-else-if="isSubscribed">
    <div class="hint">You’re subscribed on the web. Manage it from your web account page.</div>
  </template>

  <!-- Free on iOS -->
  <template v-else>
    <button
      v-if="iosPackage && !iapError"
      type="button"
      class="btn-primary"
      :disabled="iapLoading || planLoading"
      @click="purchaseStandardIos"
    >
      {{ iapLoading ? 'Loading…' : 'Subscribe' }}
    </button>
    <button
      type="button"
      class="btn-secondary"
      :disabled="iapLoading"
      @click="restoreIos"
    >
      Restore
    </button>
    <div v-if="!iosPackage || iapError" class="hint">Store unavailable. Try again shortly.</div>
  </template>
</div>


  <!-- Fallback (Android/SSR) -->
  <div v-else aria-hidden="true"></div>
</div>



        </header>

        <ul class="bullets">
          <li><strong>Unlock:</strong> 25+ calibrated robots</li>
          <li><strong>AI tools:</strong> Describe motion, Autofill, Cycle-time analysis</li>
          <li><strong>Motion builder:</strong> Waypoints & Segments</li>
          <li><strong>Customization:</strong> delays, approach offset, blended motion toggle</li>
        </ul>

        <div class="robots-wrap">
          <button class="expander" type="button" @click="showStdRobots = !showStdRobots" :aria-expanded="showStdRobots">
            <span class="chev" :class="{open: showStdRobots}">▸</span>
            Robots
          </button>
          <transition name="fade">
            <div v-if="showStdRobots" class="chips">
              <span v-for="r in standardRobots" :key="r.id" class="chip">{{ r.label }}</span>
            </div>
          </transition>
        </div>

        <p class="hint">Contact us if there are specific models you’d like to see — we’ll consider calibrating and adding them.</p>
      </section>

      <!-- Pro -->
      <section class="plan-card">
        <header class="plan-head">
          <div class="plan-title">
            <div class="name">Pro</div>
            <div class="coming">Coming soon</div>
          </div>
          <div class="cta-area" aria-hidden="true"></div>
        </header>

        <ul class="bullets">
          <li><strong>Unlock:</strong> More robots (expanded catalog)</li>
          <li><strong>Save & export</strong> scenarios</li>
          <li><strong>Team features</strong> (TBD)</li>
          <li><strong>Plan:</strong> Pro</li>
        </ul>
      </section>
    </div>
  </BaseLayout>
</template>

<style scoped>
/* Grid */
.plans-grid{
  display:grid;
  grid-template-columns: repeat(3, minmax(0,1fr));
  gap: 22px;
}
@media (max-width: 1100px){ .plans-grid{ grid-template-columns: 1fr; } }

/* Cards */
.plan-card{
  background:#fff;
  border:1px solid #e5e7eb;
  border-radius:16px;
  padding:18px 18px 16px;
  box-shadow:0 1px 2px rgba(16,24,40,0.05);
}
.plan-card.highlight{
  border-color:#dbeafe;
  box-shadow:0 1px 2px rgba(37,99,235,.08);
}

/* Head area (consistent height so features align) */
.plan-head{
  display:flex;
  justify-content:space-between;
  gap:12px;
  align-items:flex-start;
  min-height: 92px;
}

.plan-title .name{ font-weight:800; font-size:20px; line-height:1.15; }
.price-inline{ margin-top:4px; }
.price-inline .price{ font-weight:800; font-size:20px; margin-right:4px; }
.price-inline .per{ color:#6b7280; font-weight:600; font-size:14px; }
.coming{ color:#6b7280; font-weight:700; font-size:14px; }

/* CTA */
.cta-area{ display:flex; align-items:flex-start; gap:8px; flex-wrap:wrap; }
.btn-primary{
  background:#0f172a; color:#fff; border:1px solid #0f172a;
  border-radius:10px; padding:.55rem .9rem; font-weight:700; cursor:pointer;
}
.btn-primary:hover{ opacity:.92; }
.status-pill{
  margin-top:6px; background:#e8f5ec; color:#14532d; border:1px solid #cdebd4;
  border-radius:999px; padding:.22rem .55rem; font-weight:800; font-size:11.5px;
}

/* Lists */
.bullets{ margin:.35rem 0 .65rem 1.1rem; }
.bullets li{ margin:.25rem 0; }

/* Robots expander */
.robots-wrap{
  border:1px solid #eef2f7; background:#fafafa;
  border-radius:12px; padding:8px 10px;
}
.expander{
  width:100%; display:flex; align-items:center; gap:8px;
  background:transparent; border:0; padding:6px 2px;
  font-weight:800; cursor:pointer; text-align:left;
}
.chev{ display:inline-block; transition: transform .18s ease; }
.chev.open{ transform: rotate(90deg); }

.chips{ display:flex; flex-wrap:wrap; gap:8px; margin-top:8px; }
.chip{
  background:#f3f4f6; border:1px solid #e5e7eb;
  border-radius:999px; padding:.35rem .6rem; font-weight:600;
}

/* Notes */
.foot-note{ color:#6b7280; margin-top:10px; font-size:.9rem; }
.hint{ color:#475569; margin-top:10px; font-size:.82rem; }

/* Fade */
.fade-enter-active,.fade-leave-active{ transition: opacity .15s ease; }
.fade-enter-from,.fade-leave-to{ opacity:0; }
.cta-ios { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.btn-secondary{
  background:#fff; color:#0f172a; border:1px solid #0f172a;
  border-radius:10px; padding:.55rem .9rem; font-weight:700; cursor:pointer;
}
.btn-secondary:hover{ background:#f6f7f9; }

</style>
